import { Button } from 'react-bootstrap';
import type { AnyForm } from '../forms/fields';

export interface SegmentOption {
  value: string;
  label: string;
  /** react-bootstrap variant; the selected option is solid, the others outlined */
  variant?: string;
  icon?: string;
}

/**
 * A visible choice between a few options (instead of a dropdown): radio buttons styled as a button group,
 * bound to a react-hook-form field. The value is written with setValue, so it works for a field that is
 * only ever set by this control.
 */
export function Segmented({ form, name, label, required, options, disabled, hint, hideLabel, compact }: {
  form: AnyForm; name: string; label: string; required?: boolean; options: SegmentOption[]; disabled?: boolean; hint?: string; hideLabel?: boolean; compact?: boolean;
}) {
  const value = String(form.watch(name) ?? '');
  const error = (form.formState.errors as Record<string, { message?: string } | undefined>)[name]?.message;
  return (
    <div className={compact ? 'mb-0' : 'mb-3'}>
      <div className={hideLabel ? 'visually-hidden' : 'form-label'}>{label}{required && <span className="text-danger"> *</span>}</div>
      <div className={`btn-group ${compact ? 'btn-group-sm' : 'w-100'}`} role="radiogroup" aria-label={label}>
        {options.map((o) => {
          const variant = o.variant ?? 'primary';
          return (
            <Button key={o.value} type="button" role="radio" aria-checked={value === o.value} disabled={disabled}
              variant={value === o.value ? variant : `outline-${variant}`}
              onClick={() => form.setValue(name, o.value, { shouldValidate: form.formState.isSubmitted, shouldDirty: true })}>
              {o.icon && <i className={`bi bi-${o.icon} me-1`} aria-hidden />}{o.label}
            </Button>
          );
        })}
      </div>
      {hint && <div className="form-text">{hint}</div>}
      {error && <div className="text-danger small mt-1">{error}</div>}
    </div>
  );
}
